import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LanguageSwitcher from '@/components/LanguageSwitcher';
import Home from '@/pages/Home';
import HomeCN from '@/pages/HomeCN';

/**
 * 智能 Home 组件
 * 根据 i18next-browser-languagedetector 检测到的语言自动渲染对应版本
 * - 中文（zh、zh-CN）-> 渲染 HomeCN 组件
 * - 其他语言 -> 渲染 Home 组件
 */
export const SmartHome: React.FC = () => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<string>('');

  useEffect(() => {
    // 监听语言变化
    const handleLanguageChange = (lng: string) => {
      console.log('语言检测结果:', lng);
      setCurrentLanguage(lng);
      setIsLoading(false);
    };

    // 如果 i18n 已经初始化完成
    if (i18n.isInitialized) {
      handleLanguageChange(i18n.language);
    } else {
      // 等待 i18n 初始化完成
      i18n.on('initialized', handleLanguageChange);
    }

    // 监听语言切换事件
    i18n.on('languageChanged', handleLanguageChange);

    // 清理事件监听器
    return () => {
      i18n.off('initialized', handleLanguageChange);
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // 判断是否为中文
  const isChinese = (language: string): boolean => {
    const chineseLanguages = ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'];
    return chineseLanguages.some((lang) => language.toLowerCase().startsWith(lang.toLowerCase()));
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">正在检测语言...</p>
        </div>
      </div>
    );
  }

  // 根据语言渲染对应组件
  const shouldRenderChinese = isChinese(currentLanguage);

  // 开发环境下显示调试信息
  if (import.meta.env.DEV) {
    console.log('SmartHome 渲染信息:', {
      currentLanguage,
      shouldRenderChinese,
      component: shouldRenderChinese ? 'HomeCN' : 'Home',
    });
  }

  return (
    <>
      {/* 开发环境下显示语言切换器 */}
      {import.meta.env.DEV && <LanguageSwitcher />}

      {/* 根据语言渲染对应的 Home 组件 */}
      {shouldRenderChinese ? <HomeCN /> : <Home />}
    </>
  );
};

export default SmartHome;
